import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

export const achievementNotificationService = {
  // Achievement notifications are now handled by Lambda functions
  // This method is kept for backward compatibility but does nothing
  async createAchievementNotification(userId, achievement, options = {}) {
    return { id: 'lambda-handled', message: 'Notification handled by Lambda' };
  },

  // Get achievement notifications for user
  async getAchievementNotifications(userId) {
    try {
      const LIST_NOTIFICATIONS = `
        query ListNotifications($filter: ModelNotificationFilterInput) {
          listNotifications(filter: $filter) {
            items {
              id
              userID
              type
              message
              isRead
              metadata
              createdAt
              updatedAt
            }
          }
        }
      `;

      const result = await getClient().graphql({
        query: LIST_NOTIFICATIONS,
        variables: {
          filter: {
            userID: { eq: userId },
            metadata: { contains: '"type":"achievement"' }
          }
        }
      });

      return result.data?.listNotifications?.items || [];
    } catch (error) {
      console.error('Error fetching achievement notifications:', error);
      return [];
    }
  },

  // Mark achievement notification as read
  async markAchievementNotificationRead(notificationId) {
    try {
      const UPDATE_NOTIFICATION = `
        mutation UpdateNotification($input: UpdateNotificationInput!) {
          updateNotification(input: $input) {
            id
            isRead
            updatedAt
          }
        }
      `;

      const result = await getClient().graphql({
        query: UPDATE_NOTIFICATION,
        variables: {
          input: {
            id: notificationId,
            isRead: true
          }
        }
      });

      return result.data?.updateNotification;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return null;
    }
  },

  // Create share notification when user shares achievement
  async createShareNotification(userId, achievement, shareType = 'feed') {
    try {
      const CREATE_NOTIFICATION = `
        mutation CreateNotification($input: CreateNotificationInput!) {
          createNotification(input: $input) {
            id
            userID
            type
            message
            isRead
            metadata
            createdAt
          }
        }
      `;

      const notification = {
        userID: userId,
        type: 'INFO',
        message: `📢 You shared your achievement: ${achievement.achievementName || achievement.achievementType}`,
        isRead: false,
        metadata: JSON.stringify({
          type: 'achievement_share',
          achievementID: achievement.id,
          achievementType: achievement.achievementType,
          shareType: shareType,
          action: 'view_post'
        })
      };

      const result = await getClient().graphql({
        query: CREATE_NOTIFICATION,
        variables: { input: notification }
      });

      return result.data?.createNotification;
    } catch (error) {
      console.error('Error creating share notification:', error);
      return null;
    }
  },

  // Trigger browser notification for achievement unlock
  triggerBrowserNotification(achievement, options = {}) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(`🏆 Achievement Unlocked!`, {
        body: `${achievement.achievementName || achievement.achievementType}`,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `achievement-${achievement.id}`,
        requireInteraction: true,
        ...options
      });

      notification.onclick = () => {
        window.focus();
        // Navigate to achievements page
        if (window.location.pathname !== '/achievements') {
          window.location.href = '/achievements';
        }
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    }
  },

  // Request notification permission
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  },

  // Check if notifications are supported and enabled
  isNotificationSupported() {
    return 'Notification' in window;
  },

  isNotificationEnabled() {
    return 'Notification' in window && Notification.permission === 'granted';
  }
};